import { useState, useEffect, useRef } from 'react';

interface SearchableSelectProps<T> {
  items: T[];
  onSelect: (item: T) => void;
  getItemLabel: (item: T) => string;
  getItemId: (item: T) => number | string;
  getItemCode?: (item: T) => string;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  excludeIds?: Set<number | string>;
  filterFn?: (item: T, searchTerm: string) => boolean;
}

export default function SearchableSelect<T>({
  items,
  onSelect,
  getItemLabel,
  getItemId,
  getItemCode,
  placeholder = 'Buscar...',
  label,
  disabled = false,
  excludeIds = new Set(),
  filterFn,
}: SearchableSelectProps<T>) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredItems, setFilteredItems] = useState<T[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredItems([]);
      return;
    }

    const filtered = items.filter((item) => {
      // Excluir elementos ya seleccionados
      if (excludeIds.has(getItemId(item))) {
        return false;
      }

      // Usar función de filtro personalizada si existe
      if (filterFn) {
        return filterFn(item, searchTerm);
      }

      // Filtro por defecto: buscar en código y nombre
      const label = getItemLabel(item).toLowerCase();
      const code = getItemCode ? getItemCode(item).toLowerCase() : '';
      const search = searchTerm.toLowerCase();
      
      return label.includes(search) || code.includes(search);
    });

    setFilteredItems(filtered.slice(0, 10)); // Limitar a 10 resultados
  }, [searchTerm, items, excludeIds, filterFn, getItemLabel, getItemCode, getItemId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (item: T) => {
    onSelect(item);
    setSearchTerm('');
    setIsOpen(false);
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {label && (
        <label className="input-label" style={{ marginBottom: '0.5rem', display: 'block' }}>
          {label}
        </label>
      )}
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="text"
          className="input"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => {
            if (searchTerm.trim()) {
              setIsOpen(true);
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
          style={{ width: '100%' }}
        />
        {isOpen && filteredItems.length > 0 && (
          <div
            ref={dropdownRef}
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 1000,
              backgroundColor: 'var(--bg-primary)',
              border: '1px solid var(--border-color)',
              borderRadius: '4px',
              marginTop: '4px',
              maxHeight: '300px',
              overflowY: 'auto',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            }}
          >
            {filteredItems.map((item) => {
              const id = getItemId(item);
              const label = getItemLabel(item);
              const code = getItemCode ? getItemCode(item) : '';
              return (
                <div
                  key={String(id)}
                  onClick={() => handleSelect(item)}
                  style={{
                    padding: '0.75rem 1rem',
                    cursor: 'pointer',
                    borderBottom: '1px solid var(--border-color)',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <div style={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                    {code && <span style={{ color: 'var(--text-secondary)', marginRight: '0.5rem' }}>{code}</span>}
                    {label}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

